import React, { useState, useEffect, useMemo } from 'react';
import PlotlyChart from '../../components/charts/PlotlyChart';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { hfService } from '../../services/hfService';
import { formatPct, formatNum, parseDfJson } from '../../utils/formatters';
import { getFundColor } from '../../utils/colors';

const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

const CUM_PERIODS = [
  { label: '6M', months: 6 },
  { label: '1Y', months: 12 },
  { label: '2Y', months: 24 },
  { label: '3Y', months: 36 },
  { label: '5Y', months: 60 },
  { label: '10Y', months: 120 },
  { label: 'All', months: null },
];

const ComparisonPage = ({ analyticsParams, fundList, focusFund, cachedFetch }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rebased, setRebased] = useState(false);
  const [viewMode, setViewMode] = useState('all');
  const [showBM, setShowBM] = useState(true);
  const [cumPeriod, setCumPeriod] = useState('focus'); // default: focus fund inception

  useEffect(() => {
    if (!analyticsParams?.dataset_id) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const fetchFn = () => hfService.getComparison({ ...analyticsParams, focus_fund: focusFund || undefined });
        const res = cachedFetch ? await cachedFetch('comparison-' + (focusFund || ''), fetchFn) : await fetchFn();
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [analyticsParams, focusFund]);

  const colorMap = data?.colors || {};

  // Cumulative returns chart traces with period filtering
  const cumulativeTraces = useMemo(() => {
    if (!data?.cumulative) return [];
    const { index: dates, columns: fundNames, data: rows } = parseDfJson(data.cumulative);
    const xDates = dates.map(d => new Date(d));

    // Find focus fund inception date (first non-null data point)
    const focusIdx = focusFund ? fundNames.indexOf(focusFund) : -1;
    let focusInceptionDate = null;
    if (focusIdx >= 0) {
      for (let i = 0; i < rows.length; i++) {
        if (rows[i][focusIdx] != null) {
          focusInceptionDate = xDates[i];
          break;
        }
      }
    }

    // Period filter helper: slice and rebase
    const sliceAndRebase = (xArr, yArr) => {
      let cutoff = null;

      if (cumPeriod === 'focus') {
        // Default: start from focus fund inception
        cutoff = focusInceptionDate;
      } else if (cumPeriod === null) {
        // "All" -- no filter
        return { x: xArr, y: yArr };
      } else {
        // Numeric months
        cutoff = new Date(xArr[xArr.length - 1]);
        cutoff.setMonth(cutoff.getMonth() - cumPeriod);
      }

      if (!cutoff) return { x: xArr, y: yArr };
      const startIdx = xArr.findIndex(d => d >= cutoff);
      if (startIdx <= 0) return { x: xArr, y: yArr };

      const slicedX = xArr.slice(startIdx);
      const baseVal = yArr[startIdx];
      const slicedY = yArr.slice(startIdx).map(v =>
        v != null && baseVal != null ? (1 + v) / (1 + baseVal) - 1 : null
      );
      return { x: slicedX, y: slicedY };
    };

    const traces = fundNames.map((name, ci) => {
      const yRaw = rows.map(row => row[ci]);
      const { x, y } = sliceAndRebase(xDates, yRaw);

      // Apply rebasing to 100 if checkbox is on
      let yFinal = y;
      if (rebased) {
        const first = y.find(v => v != null);
        if (first != null && first !== 0) {
          yFinal = y.map(v => (v != null ? ((1 + v) / (1 + first)) * 100 : null));
        } else {
          yFinal = y.map(v => (v != null ? (1 + v) * 100 : null));
        }
      }

      // Filter out nulls so lines start from each fund's inception
      const filtered = x.reduce((acc, xi, i) => {
        if (yFinal[i] != null && !isNaN(yFinal[i])) { acc.x.push(xi); acc.y.push(yFinal[i]); }
        return acc;
      }, { x: [], y: [] });

      const displayName = isMobile ? name.split(' ')[0] : name;
      return {
        x: filtered.x,
        y: filtered.y,
        type: 'scatter',
        mode: 'lines',
        name: displayName,
        _fullName: name,
        line: { color: getFundColor(name, colorMap), width: 2 },
        hovertemplate: rebased
          ? `${name}: %{y:.1f}<extra></extra>`
          : `${name}: %{y:.2%}<extra></extra>`,
      };
    });

    // SPX overlay
    if (data.spx_cumulative) {
      const spx = parseDfJson(data.spx_cumulative);
      let spxDates = spx.index.map(d => new Date(d));
      let spxVals = spx.data.map(row => (Array.isArray(row) ? row[0] : row));

      // Clip SPX to earliest fund data point
      const earliestFund = xDates.find((d, di) =>
        fundNames.some((_, ci) => rows[di]?.[ci] != null)
      );
      if (earliestFund) {
        const clipIdx = spxDates.findIndex(d => d >= earliestFund);
        if (clipIdx > 0) {
          spxDates = spxDates.slice(clipIdx);
          const baseVal = spxVals[clipIdx];
          spxVals = spxVals.slice(clipIdx).map(v =>
            v != null && baseVal != null ? (1 + v) / (1 + baseVal) - 1 : null
          );
        }
      }

      const { x, y } = sliceAndRebase(spxDates, spxVals);

      let yFinal = y;
      if (rebased) {
        const first = y.find(v => v != null);
        if (first != null && first !== 0) {
          yFinal = y.map(v => (v != null ? ((1 + v) / (1 + first)) * 100 : null));
        } else {
          yFinal = y.map(v => (v != null ? (1 + v) * 100 : null));
        }
      }

      // Filter out nulls
      const pairs = x.map((d, i) => [d, yFinal[i]]).filter(([, v]) => v != null && !isNaN(v));
      if (pairs.length > 0) {
        traces.push({
          x: pairs.map(([d]) => d),
          y: pairs.map(([, v]) => v),
          type: 'scatter',
          mode: 'lines',
          name: isMobile ? 'SPX' : 'SPX Index',
          _fullName: 'SPX Index',
          line: { color: '#DC143C', width: 2, dash: 'dash' },
          hovertemplate: rebased
            ? 'SPX: %{y:.1f}<extra></extra>'
            : 'SPX: %{y:.2%}<extra></extra>',
        });
      }
    }

    // Filter traces based on viewMode and showBM
    return traces.filter(t => {
      const name = t.name;
      const isSpx = name === 'SPX Index' || name === 'SPX';
      if (isSpx) return showBM;
      if (viewMode === 'focus' && focusFund) {
        const fullMatch = t._fullName === focusFund;
        const mobileMatch = name === focusFund.split(' ')[0];
        return fullMatch || mobileMatch;
      }
      return true;
    });
  }, [data, rebased, colorMap, viewMode, focusFund, showBM, cumPeriod]);

  // Risk-return scatter — uses backend-computed data for each period
  const [scatterPeriod, setScatterPeriod] = useState('focus');
  const scatterTraces = useMemo(() => {
    if (!data) return [];

    // Pick the right scatter data based on period
    let scatterData;
    if (scatterPeriod === 'focus') {
      scatterData = data.scatter; // Backend computes from focus fund inception
    } else if (scatterPeriod === null) {
      scatterData = data.scatter; // fallback
    } else {
      // Period lookback: 6, 12, 36, 60, 120 months
      const key = { 6: '6m', 12: '1y', 24: '2y', 36: '3y', 60: '5y', 120: '10y' }[scatterPeriod];
      scatterData = data.scatter_periods?.[key] || data.scatter;
    }
    if (!scatterData || !scatterData.length) return [];

    const traces = [];
    const filtered = viewMode === 'focus' && focusFund
      ? scatterData.filter(s => s.fund === focusFund)
      : scatterData;

    if (filtered.length > 0) {
      traces.push({
        x: filtered.map(s => s.risk),
        y: filtered.map(s => s.return),
        text: filtered.map(s => s.fund),
        type: 'scatter', mode: 'markers+text',
        textposition: 'top center', textfont: { size: 10 },
        marker: {
          color: filtered.map(s => s.color || getFundColor(s.fund, colorMap)),
          size: 12, symbol: 'circle',
        },
        hovertemplate: '%{text}<br>Risk: %{x:.2%}<br>Return: %{y:.2%}<extra></extra>',
        showlegend: false,
      });
    }

    if (data.spx_scatter && showBM) {
      traces.push({
        x: [data.spx_scatter.risk], y: [data.spx_scatter.return], text: ['SPX'],
        type: 'scatter', mode: 'markers+text',
        textposition: 'top center', textfont: { size: 10, color: '#DC143C' },
        marker: { color: '#DC143C', size: 14, symbol: 'diamond' },
        hovertemplate: 'SPX<br>Risk: %{x:.2%}<br>Return: %{y:.2%}<extra></extra>',
        name: 'SPX Index', showlegend: false,
      });
    }
    return traces;
  }, [data, colorMap, viewMode, focusFund, showBM, scatterPeriod]);

  // Stats table
  const statsTable = useMemo(() => {
    if (!data?.stats) return null;
    return parseDfJson(data.stats);
  }, [data]);

  const isPctCol = (colName) => {
    const lower = colName.toLowerCase();
    return lower.includes('return') || lower.includes('drawdown') || lower.includes('vol')
      || lower.includes('std') || lower.includes('pct') || lower.includes('%')
      || lower.includes('best') || lower.includes('worst');
  };

  if (loading) return <LoadingSpinner message="Loading comparison data..." />;

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-2">Failed to load comparison data</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setViewMode('all')} className={`px-3 py-1.5 text-xs rounded-md transition-colors ${viewMode === 'all' ? 'bg-brand-gold text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-400 border border-gray-200 dark:border-gray-700'}`}>All Funds</button>
        <button onClick={() => setViewMode('focus')} className={`px-3 py-1.5 text-xs rounded-md transition-colors ${viewMode === 'focus' ? 'bg-brand-gold text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-400 border border-gray-200 dark:border-gray-700'}`}>{focusFund ? focusFund.split(' ')[0] + ' Only' : 'Focus Fund'}</button>
        <span className="w-px h-6 bg-gray-300 dark:bg-gray-700 self-center" />
        <button onClick={() => setShowBM(!showBM)} className={`px-3 py-1.5 text-xs rounded-md transition-colors ${showBM ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-400 border border-gray-200 dark:border-gray-700'}`}>{showBM ? 'SPX \u2713' : 'SPX'}</button>
      </div>

      {/* Cumulative Returns Chart */}
      {cumulativeTraces.length > 0 && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Cumulative Returns Comparison
              {cumPeriod === 'focus' ? ' (SI Focus Fund)' : cumPeriod === null ? ' (All)' : ` (${CUM_PERIODS.find(p => p.months === cumPeriod)?.label || ''})`}
            </h3>
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={rebased}
                onChange={(e) => setRebased(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 text-brand-gold focus:ring-brand-gold/30"
              />
              Rebase to 100
            </label>
          </div>
          <div className="flex gap-1 items-center mb-3 flex-wrap">
            <button
              onClick={() => setCumPeriod('focus')}
              className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                cumPeriod === 'focus'
                  ? 'bg-brand-gold text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >Inception</button>
            {CUM_PERIODS.map(p => (
              <button
                key={p.label}
                onClick={() => setCumPeriod(p.months)}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                  cumPeriod === p.months
                    ? 'bg-brand-gold text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <PlotlyChart
            data={cumulativeTraces}
            layout={{
              yaxis: {
                title: rebased ? 'Rebased (100)' : 'Cumulative Return',
                tickformat: rebased ? '.0f' : '.0%',
              },
              xaxis: { title: '' },
              legend: {
                orientation: 'h',
                yanchor: 'bottom',
                y: -0.25,
                xanchor: 'center',
                x: 0.5,
                font: { size: isMobile ? 9 : 12 },
              },
            }}
            style={{ minHeight: 350 }}
          />
        </div>
      )}

      {/* Risk-Return Scatter */}
      {(scatterTraces.length > 0 || data?.returns_df) && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Risk-Return Profile
              {scatterPeriod === 'focus' ? ' (SI Focus Fund)' : scatterPeriod === null ? ' (SI Each Fund)' : ` (${CUM_PERIODS.find(p => p.months === scatterPeriod)?.label || ''})`}
            </h3>
          </div>
          <div className="flex gap-1 items-center mb-3 flex-wrap">
            <button onClick={() => setScatterPeriod('focus')}
              className={`px-2.5 py-1 text-xs rounded-md transition-colors ${scatterPeriod === 'focus' ? 'bg-brand-gold text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`}
            >Inception</button>
            {CUM_PERIODS.map(p => (
              <button key={p.label} onClick={() => setScatterPeriod(p.months)}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${scatterPeriod === p.months ? 'bg-brand-gold text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`}
              >{p.label === 'All' ? 'SI All' : p.label}</button>
            ))}
          </div>
          <PlotlyChart
            data={scatterTraces}
            layout={{
              xaxis: { title: 'Annualized Risk (Std Dev)', tickformat: '.1%', rangemode: 'tozero' },
              yaxis: { title: 'Annualized Return', tickformat: '.1%', rangemode: 'tozero' },
              hovermode: 'closest',
            }}
            style={{ minHeight: 350 }}
          />
        </div>
      )}

      {/* Comparison Stats Table */}
      {statsTable && statsTable.index.length > 0 && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Comparison Statistics</h3>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-3 py-2.5 text-left sticky left-0 z-10 bg-gray-50 dark:bg-gray-800/50">Fund</th>
                  {statsTable.columns.map((col) => (
                    <th key={col} className="px-3 py-2.5 text-right whitespace-nowrap">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {statsTable.index.map((fund, ri) => (
                  <tr key={fund} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                    <td className="px-3 py-2 whitespace-nowrap text-gray-800 dark:text-gray-200">
                      <span
                        className="inline-block mr-2 rounded-full"
                        style={{
                          width: 10,
                          height: 10,
                          backgroundColor: getFundColor(fund, colorMap),
                        }}
                      />
                      {fund}
                    </td>
                    {statsTable.data[ri].map((val, ci) => (
                      <td key={ci} className="px-3 py-2 text-right tabular-nums whitespace-nowrap text-gray-700 dark:text-gray-300">
                        {val == null
                          ? 'N/A'
                          : isPctCol(statsTable.columns[ci])
                            ? formatPct(val)
                            : formatNum(val)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonPage;
